import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  PageLayout,
  Typography,
  TYPOGRAPHY_SIZE,
  ButtonsWrapper,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  DeleteModal,
  EmptyContainer,
  EMPTY_ILLUSTRATIONS,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import PermissionCheck from 'src/components/permission-check';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { useGetVacancies } from 'src/hooks/vacancies-patterns/get-vacancies.hook';
import { useChangeVacancyStatus } from 'src/hooks/vacancies-patterns/change-vacancy-status.hook';
import { useDeleteVacancyById } from 'src/hooks/vacancies-patterns/delete-vacancy-by-id.hook';
import { getFiltersCount } from 'src/common/helpers/common.helpers';

import { HeaderContainer } from './vacancies.styles';
import { VacanciesListTable } from './components/vacancies-list-table';
import { VACANCIES_PAGES } from './vacancies.constants';
import { VacanciesTableFilter } from './components/vacancies-table-filter';
import { VACANCIES_MODALS } from './vacancies.constants';
import { IVacanciesFilter, IVacancy } from './vacancies.interfaces';
import { VacancyConfirmationModal } from './components/vacancy-confirmation-modal';

const Vacancies: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'vacancies']);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [filters, setFilters] = useState<IVacanciesFilter>({});
  const [currentVacancy, setCurrentVacancy] = useState<IVacancy | null>(null);
  const { vacancies, paginationConfig } = useGetVacancies(filters);
  const { deleteVacancy } = useDeleteVacancyById();
  const { changeVacancyStatus } = useChangeVacancyStatus({
    onSuccess: () => {
      setCurrentVacancy(null);
      setActiveModal(null);
    },
  });

  const closeModal = () => {
    setCurrentVacancy(null);
    setActiveModal(null);
  };

  const submitDeleteModal = () => {
    deleteVacancy({
      variables: {
        vacancyId: currentVacancy?._id,
      },
      onCompleted: () => {
        closeModal();
        if (vacancies.length <= 1 && paginationConfig.current > 1) {
          paginationConfig.onChange(paginationConfig.current - 1);
        }
      },
    });
  };

  const submitStatusModal = () => {
    changeVacancyStatus({
      variables: {
        payload: {
          vacancyId: currentVacancy?._id,
          status: currentVacancy?.status,
        },
      },
    });
  };

  return (
    <PageLayout pageTitle={t('vacancies:labels.vacancies')}>
      <VacanciesTableFilter
        isOpened={activeModal === VACANCIES_MODALS.tableFilter}
        onClose={closeModal}
        filters={filters}
        setFilters={setFilters}
      />
      <DeleteModal
        translate={t}
        title={t('vacancies:deleteVacancy.title')}
        subTitle={t('vacancies:deleteVacancy.subtitle', {
          vacancyName: currentVacancy?.job.title,
        })}
        isOpened={activeModal === VACANCIES_MODALS.deleteVacancy}
        onClose={closeModal}
        onSubmit={submitDeleteModal}
      />
      <VacancyConfirmationModal
        activeModal={activeModal}
        onClose={closeModal}
        onSubmit={submitStatusModal}
      />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('vacancies:labels.vacancies')}
        </Typography>
        <ButtonsWrapper>
          <Button
            type="primary"
            ghost
            icon={ICONS.filterAlt}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setActiveModal(VACANCIES_MODALS.tableFilter)}
          >
            {`${t('common:buttons.filterBy')} ${getFiltersCount(filters)}`}
          </Button>
          <PermissionCheck
            action={PERMISSION_ACTIONS.create}
            permission={PLATFORM_PERMISSIONS.vacanciesManagement}
          >
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => navigate(`${ROUTES.vacancies}/${VACANCIES_PAGES.createVacancy}`)}
            >
              {t('vacancies:buttons.createNewVacancy')}
            </Button>
          </PermissionCheck>
        </ButtonsWrapper>
      </HeaderContainer>
      {vacancies.length > 0 ? (
        <VacanciesListTable
          dataSource={vacancies}
          paginationConfig={paginationConfig}
          handleModals={(modal, vacancy) => {
            setActiveModal(modal);
            setCurrentVacancy(vacancy);
          }}
        />
      ) : (
        <EmptyContainer
          emptyTitle={t('common:emptyContainer.emptyTitle')}
          contentText={t('common:emptyContainer.contentText')}
          illustration={EMPTY_ILLUSTRATIONS.dataAnalysis}
        />
      )}
    </PageLayout>
  );
};

export default Vacancies;
